import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UserService } from 'src/app/services/user/user.service';

/** La cuenta bancaria del comercio (CONTRATO-PUESTA-EN-MARCHA.md, «C»): la llena el ejecutivo, y al
 *  hacer el alta pasa a `payment_accounts` como la principal. `banco` y `banco_nombre` no se
 *  escriben a mano: salen del catálogo, por los 4 primeros dígitos de `numero`. */
export interface CuentaBancariaComercio {
  banco: string;
  banco_nombre: string;
  tipo: 'CORRIENTE' | 'AHORRO';
  numero: string;
  titular: string;
  rif_titular: string;
  moneda: 'VES' | 'USD';
}

interface BancoCatalogo { codigo: string; nombre: string; }

const CUENTA_VACIA: CuentaBancariaComercio = {
  banco: '', banco_nombre: '', tipo: 'CORRIENTE', numero: '', titular: '', rif_titular: '', moneda: 'VES',
};

/** Para comparar «¿es el mismo?» sin que un guión, un punto o una mayúscula cuenten como
 *  diferencia: ni el RIF («J-40123456-7» vs «J401234567») ni la razón social («Mundo Total, C.A.»
 *  vs «MUNDO TOTAL CA») deben disparar un aviso falso. */
const normalizar = (t: string | null | undefined): string =>
  String(t || '').toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Z0-9]/g, '');

/**
 * El formulario de la cuenta bancaria del comercio — componente NUEVO
 * (CONTRATO-PUESTA-EN-MARCHA.md, «D»). Lo embebe «Completar mi parte» (paso Cuenta bancaria, entre
 * Contactos y Documentos); el padre decide cuándo guardar (con `actualizar` o lo que exponga el
 * núcleo para CUENTA) y le pasa el resultado por `valor` para que, si el servidor lo rechaza, lo
 * escrito no se pierda.
 *
 * El banco es SOLO LECTURA: nunca se elige a mano. Se resuelve de los 4 primeros dígitos del
 * número contra el catálogo del núcleo (`UserService.getBanks`, `/api/invoice-credit/banks`), el
 * mismo que ya usan los reportes de pagos. Sin número o con un prefijo que no está en el catálogo,
 * queda en blanco con su propio aviso — nunca a medias ni con un dato inventado.
 */
@Component({
  selector: 'app-cuenta-bancaria-form',
  templateUrl: './cuenta-bancaria.component.html',
  styleUrls: ['./cuenta-bancaria.component.css'],
})
export class CuentaBancariaComponent implements OnInit, OnChanges {

  /** La cuenta ya cargada (al reabrir el paso) o `null`/`undefined` si todavía no hay ninguna. */
  @Input() valor: Partial<CuentaBancariaComercio> | null = null;
  /** Razón social del comercio, para el aviso de no coincidencia. Sin ella, no se avisa. */
  @Input() razonSocial = '';
  /** RIF del comercio, para el mismo aviso. */
  @Input() rif = '';

  /** Cada cambio en cualquier campo, con la forma completa que espera el núcleo. */
  @Output() cambio = new EventEmitter<CuentaBancariaComercio>();

  form: CuentaBancariaComercio = { ...CUENTA_VACIA };

  bancos: BancoCatalogo[] = [];
  bancosCargando = true;

  constructor(private _user: UserService) { }

  ngOnInit(): void { this.cargarBancos(); this.precargarTitular(); }

  /** El padre puede pasar `valor` después de que el componente ya arrancó (p. ej. al terminar de
   *  cargar la solicitud): se fusiona, nunca se pisa lo que el ejecutivo ya esté escribiendo con un
   *  vacío que llegue tarde. */
  ngOnChanges(cambios: SimpleChanges): void {
    if (cambios['valor'] && this.valor) this.form = { ...CUENTA_VACIA, ...this.valor };
    if (cambios['valor'] || cambios['razonSocial'] || cambios['rif']) this.precargarTitular();
  }

  /** Juan, 2026-10-08: «precárgame el mismo del comercio y, si lo quiere cambiar, que lo pueda
   *  cambiar». El titular y su RIF vacíos se llenan con la razón social y el RIF del comercio; lo
   *  que ya esté escrito no se toca. Se avisa al padre en el siguiente ciclo (no dentro del cambio). */
  private precargarTitular(): void {
    let lleno = false;
    if (!this.form.titular && this.razonSocial) { this.form.titular = this.razonSocial; lleno = true; }
    if (!this.form.rif_titular && this.rif) { this.form.rif_titular = this.rif; lleno = true; }
    if (lleno) Promise.resolve().then(() => this.emitir());
  }

  cargarBancos = async (): Promise<void> => {
    try {
      const rep = await this._user.getBanks({});
      this.bancos = (rep?.res || [])
        .filter((b: any) => b?.bankCode && b?.name)
        .map((b: any): BancoCatalogo => ({ codigo: String(b.bankCode), nombre: b.name }));
    } catch {
      this.bancos = [];
    } finally {
      this.bancosCargando = false;
      this.resolverBanco();
    }
  };

  // ══ El número y el banco que sale solo ═══════════════════════════════════

  get numeroLimpio(): string { return String(this.form.numero || '').replace(/\D/g, ''); }
  get numeroValido(): boolean { return /^\d{20}$/.test(this.numeroLimpio); }
  get prefijo(): string { return this.numeroLimpio.slice(0, 4); }
  get bancoReconocido(): boolean { return !!this.form.banco_nombre; }

  resolverBanco = (): void => {
    const encontrado = this.prefijo.length === 4 ? this.bancos.find(b => b.codigo === this.prefijo) : undefined;
    this.form.banco = encontrado ? encontrado.codigo : (this.prefijo.length === 4 ? this.prefijo : '');
    this.form.banco_nombre = encontrado ? encontrado.nombre : '';
  };

  onNumero = (valorEscrito: string): void => {
    this.form.numero = String(valorEscrito || '').replace(/\D/g, '').slice(0, 20);
    this.resolverBanco();
    this.emitir();
  };

  emitir = (): void => { this.cambio.emit({ ...this.form }); };

  // ══ El aviso de no coincidencia (avisa, no frena) ═════════════════════════

  private noCoincide = (unCampo: string, elDelComercio: string): boolean => {
    const a = normalizar(unCampo);
    const b = normalizar(elDelComercio);
    return !!a && !!b && a !== b;
  };

  get avisoTitular(): boolean { return this.noCoincide(this.form.titular, this.razonSocial); }
  get avisoRif(): boolean { return this.noCoincide(this.form.rif_titular, this.rif); }
  get hayAviso(): boolean { return this.avisoTitular || this.avisoRif; }
}
